import CampaignRoundedIcon from '@mui/icons-material/CampaignRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import RemoveShoppingCartRoundedIcon from '@mui/icons-material/RemoveShoppingCartRounded';
import { Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useEngagementSettings, useUpdateEngagementSettings } from '../../hooks';
import { toSettingsUpdate, useDomainPermissions, useFeedback, useSettingsForm } from '../../lib';
import {
  FeedbackSnackbar,
  NumberField,
  SettingsCard,
  SettingsScaffold,
  SwitchField,
} from '../components';

/** Customer engagement: reminders, abandoned cart, campaigns and limits. */
export function EngagementSettingsPage() {
  const { t } = useTranslation('settings');
  const { canView, canManage } = useDomainPermissions('engagement');
  const query = useEngagementSettings();
  const update = useUpdateEngagementSettings();
  const form = useSettingsForm(query.data);
  const { feedback, success, error: notifyError, clear } = useFeedback();

  const handleSave = () => {
    if (!form.draft) {
      return;
    }
    update.mutate(toSettingsUpdate(form.draft), {
      onSuccess: () => success(t('engagementSettings.savedFeedback')),
      onError: (error) => {
        notifyError(error.message);
        if (error.status === 409) {
          void query.refetch();
        }
      },
    });
  };

  return (
    <SettingsScaffold
      title={t('engagementSettings.title')}
      subtitle={t('engagementSettings.subtitle')}
      icon={<CampaignRoundedIcon />}
      query={query}
      canView={canView}
      canManage={canManage}
      audit={query.data}
      dirty={form.dirty}
      saving={update.isPending}
      onSave={handleSave}
      onReset={form.reset}
      feedback={<FeedbackSnackbar feedback={feedback} onClose={clear} />}
    >
      {() => {
        const draft = form.draft;
        if (!draft) {
          return null;
        }
        const disabled = !canManage;
        return (
          <Stack spacing={2.5}>
            <SettingsCard title={t('engagementSettings.remindersSection.title')} icon={<NotificationsActiveRoundedIcon />}>
              <SwitchField
                label={t('engagementSettings.remindersSection.engineLabel')}
                checked={draft.reminderEngineEnabled}
                onChange={(value) => form.setField('reminderEngineEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label={t('engagementSettings.remindersSection.importantDateLabel')}
                description={t('engagementSettings.remindersSection.importantDateDescription')}
                checked={draft.importantDateRemindersEnabled}
                onChange={(value) => form.setField('importantDateRemindersEnabled', value)}
                disabled={disabled}
              />
            </SettingsCard>

            <SettingsCard title={t('engagementSettings.abandonedCartSection.title')} icon={<RemoveShoppingCartRoundedIcon />}>
              <SwitchField
                label={t('engagementSettings.abandonedCartSection.recoveryLabel')}
                checked={draft.abandonedCartEnabled}
                onChange={(value) => form.setField('abandonedCartEnabled', value)}
                disabled={disabled}
              />
              <NumberField
                label={t('engagementSettings.abandonedCartSection.delayLabel')}
                description={t('engagementSettings.abandonedCartSection.delayDescription')}
                value={draft.abandonedCartDelayMinutes}
                onChange={(value) => form.setField('abandonedCartDelayMinutes', value)}
                disabled={disabled || !draft.abandonedCartEnabled}
                min={0}
                suffix={t('engagementSettings.abandonedCartSection.delaySuffix')}
              />
            </SettingsCard>

            <SettingsCard title={t('engagementSettings.marketingSection.title')} icon={<CampaignRoundedIcon />}>
              <SwitchField
                label={t('engagementSettings.marketingSection.campaignsLabel')}
                checked={draft.marketingCampaignsEnabled}
                onChange={(value) => form.setField('marketingCampaignsEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label={t('engagementSettings.marketingSection.promoLabel')}
                checked={draft.promoRecommendationsEnabled}
                onChange={(value) => form.setField('promoRecommendationsEnabled', value)}
                disabled={disabled}
              />
              <SwitchField
                label={t('engagementSettings.marketingSection.segmentationLabel')}
                checked={draft.segmentationEnabled}
                onChange={(value) => form.setField('segmentationEnabled', value)}
                disabled={disabled}
              />
              <NumberField
                label={t('engagementSettings.marketingSection.frequencyLabel')}
                description={t('engagementSettings.marketingSection.frequencyDescription')}
                value={draft.communicationFrequencyLimitPerDay}
                onChange={(value) => form.setField('communicationFrequencyLimitPerDay', value)}
                disabled={disabled}
                min={0}
                suffix={t('engagementSettings.marketingSection.frequencySuffix')}
              />
            </SettingsCard>
          </Stack>
        );
      }}
    </SettingsScaffold>
  );
}
